
import React from 'react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { UseFormReturn } from 'react-hook-form';
import { AutomationRuleFormData } from './schemas/automationRuleSchema';
import { RuleValidationDisplay } from './components/RuleValidationDisplay';
import { ValidationResult } from './types/automationTypes';

interface AutomationRuleFormProps {
  form: UseFormReturn<AutomationRuleFormData>;
  onSubmit: (data: AutomationRuleFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  validationResult?: ValidationResult;
  isValidating?: boolean;
  isWizardMode?: boolean;
}

export const AutomationRuleForm: React.FC<AutomationRuleFormProps> = ({
  form,
  onSubmit,
  onCancel,
  isLoading = false,
  validationResult,
  isValidating = false,
  isWizardMode = false,
}) => {
  const canSubmit = validationResult?.isValid !== false && !isValidating;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Rule Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter rule name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea placeholder="Describe what this rule does" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="enabled"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Enable Rule</FormLabel>
                <div className="text-sm text-muted-foreground">
                  Activate this automation rule
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="trigger.type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Trigger Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select trigger type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="candidate_status_change">Candidate Status Change</SelectItem>
                  <SelectItem value="time_based">Time Based</SelectItem>
                  <SelectItem value="email_received">Email Received</SelectItem>
                  <SelectItem value="interview_scheduled">Interview Scheduled</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="priority"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Priority</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {validationResult && (
          <RuleValidationDisplay 
            validationResult={validationResult}
            isValidating={isValidating}
          />
        )}

        {!isWizardMode && (
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading || !canSubmit}
            >
              {isLoading ? 'Saving...' : 'Save Rule'}
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
};
